/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useRef, useContext } from 'react';
import { graphql } from 'gatsby';
import { flushSync } from 'react-dom';
import styled from 'styled-components';
import {
  SEO,
  Button,
  DropdownMenu,
  Card,
  Testimonials,
  GoogleReviews,
  SingleColumnCarousel,
  MultiColumnCarousel,
  QuantityInput,
  ProductStructuredData
} from 'components';
import {
  Hero,
  Grid,
  InnerGrid,
  ProductDetails,
  Title,
  Description,
  Specifications,
  Spec,
  BlobWrapper,
  ButtonWrapper
} from 'components/ProductPage/Shared';
import { getAltTagFromFileName, slugify, removeDuplicatesFromArray, generateNanoId } from 'utils';
import shuffle from 'just-shuffle';
import { useWindowSize } from 'hooks';
import { customDropdownMenuStyles } from 'styles';
import { LocalContext } from 'context';

const getSimilarDoorParts = (doorPartsFromTheSameProductCollection) => {
  const similarDoorParts = shuffle(
    removeDuplicatesFromArray(Array.from(doorPartsFromTheSameProductCollection.nodes))
  ).map((node) => {
    const { id, title, images: _images, productVariants } = node;
    const slug = `${slugify(title)}-${productVariants.default.productId.toLowerCase()}`;
    const mainImage = _images?.[0];
    return (
      <Card
        key={id}
        id={id}
        slug={slug}
        image={mainImage}
        imageWidth={340}
        imageHeight={380}
        title={title}
        alt={getAltTagFromFileName(mainImage?.asset?.originalFilename || '')}
      />
    );
  });

  return similarDoorParts;
};

function DoorPartTemplate({ data }) {
  const { doorPart, doorPartsFromTheSameProductCollection, heroData } = data;
  const { productPageHeroImage } = heroData.frontmatter;
  const [quantity, setQuantity] = useState(1);
  const [similarDoorParts] = useState(getSimilarDoorParts(doorPartsFromTheSameProductCollection));
  const { setCartCounter, counterRef, cartCounterDummyRef, setQuote } = useContext(LocalContext);

  const { windowWidth } = useWindowSize();

  const blobWrapperRef = useRef();

  const [selectedProduct, setSelectedProduct] = useState({
    label: doorPart.productVariants.default.dimensions,
    value: {
      productId: doorPart.productVariants.default.productId,
      dimensions: doorPart.productVariants.default.dimensions
    },
    order: 0
  });

  const images = doorPart.images && [doorPart.images[0], ...doorPart.images.slice(1)];

  const arr = doorPart.productVariants.additional
    ? [doorPart.productVariants.default, ...doorPart.productVariants.additional]
    : [doorPart.productVariants.default];

  const dropdownOptions = arr.map(({ productId, dimensions }, i) => ({
    label: dimensions,
    value: { productId, dimensions },
    order: i
  }));

  const addToQuote = async () => {
    if (document.startViewTransition) {
      const blob = document.createElement('div');

      blob.className = 'blob';

      blobWrapperRef.current.appendChild(blob);

      const moveBlobTransition = document.startViewTransition(() =>
        cartCounterDummyRef.current.append(blob)
      );

      await moveBlobTransition.finished;

      blob.style.viewTransitionName = 'none';

      blob.remove();

      counterRef.current.style.viewTransitionName = 'cart-counter';

      const incrementCounterTransition = document.startViewTransition(() =>
        flushSync(() => {
          setCartCounter((currentValue) => currentValue + quantity);
        })
      );

      await incrementCounterTransition.finished;

      counterRef.current.style.viewTransitionName = 'none';
    } else {
      flushSync(() => {
        setCartCounter((currentValue) => currentValue + quantity);
      });
    }

    setQuote((_currentQuote) => {
      if (
        _currentQuote.some(
          ({ productId }) =>
            productId.toLowerCase() === selectedProduct.value.productId.toLowerCase()
        )
      ) {
        return _currentQuote.map((item) => {
          if (item.productId.toLowerCase() === selectedProduct.value.productId.toLowerCase()) {
            item.quantity += quantity;
          }
          return item;
        });
      }

      return [
        ..._currentQuote,
        {
          title: doorPart.title,
          productId: selectedProduct.value.productId,
          image: images[0],
          quantity,
          dimensions: {
            label: selectedProduct.value.dimensions,
            value: {
              productId: selectedProduct.value.productId,
              dimensions: selectedProduct.value.dimensions
            },
            order: selectedProduct.order
          },
          otherDimensions: dropdownOptions.filter(
            ({ value }) => value.dimensions !== selectedProduct.value.dimensions
          ),
          cartEntryId: generateNanoId()
        }
      ];
    });
  };

  return (
    <>
      <Hero
        heroImage={productPageHeroImage}
        heroImageAlt={getAltTagFromFileName(productPageHeroImage.name)}
        heroImageTitle="Door Part Product Page"
        gradientPercentage="75%"
        heroImageOffset={-100}
        filter="contrast(100%) saturate(200%)"
        removeProductNameFromPath>
        <h2>Product Page</h2>
      </Hero>
      <Grid>
        <StyledInnerGrid className="fade-in">
          <SingleColumnCarousel
            images={images}
            imageWidth={1100}
            // imageHeight={700}
            showIndicators
          />
          <ProductDetails>
            <Title id={doorPart.id}>{doorPart.title}</Title>
            <Description>{doorPart.description}</Description>
            <Specifications>Specifications</Specifications>
            {dropdownOptions.length > 1 ? (
              <>
                <h3>Available Sizes:</h3>
                <DropdownMenu
                  value={dropdownOptions.filter(
                    ({ value }) => value.dimensions === selectedProduct.value.dimensions
                  )}
                  onChange={setSelectedProduct}
                  placeholder={selectedProduct.label}
                  styles={customDropdownMenuStyles()}
                  options={dropdownOptions}
                  isSearchable={false}
                />
              </>
            ) : selectedProduct.label ? (
              <Spec style={{ marginTop: '-14px' }}>
                <h3>Size:</h3>
                <p>
                  <b>{selectedProduct.label}</b>
                </p>
              </Spec>
            ) : (
              ''
            )}
            {doorPart.finish && (
              <Spec>
                <h3>Finish:</h3>
                <p>
                  <b>{doorPart.finish}</b>
                </p>
              </Spec>
            )}
            <div>
              <h3>Quantity:</h3>
              <QuantityInput quantity={quantity} setQuantity={setQuantity} />
            </div>
            <BlobWrapper ref={blobWrapperRef} />
            <ButtonWrapper>
              <Button
                onClick={addToQuote}
                style={{
                  height: '48px',
                  width: '160px',
                  whiteSpace: 'nowrap'
                }}>
                Add To Quote
              </Button>
            </ButtonWrapper>
          </ProductDetails>
        </StyledInnerGrid>
      </Grid>
      {similarDoorParts.length &&
        (windowWidth < 995 ? (
          <SimilarDoorPartsMobile>
            <h3>View Similar Products</h3>
            {similarDoorParts.slice(0, 3)}
          </SimilarDoorPartsMobile>
        ) : (
          <StyledMultiColumnCarousel
            title="View Similar Products"
            cards={similarDoorParts}
            responsive={{
              desktop: {
                breakpoint: { max: 10000, min: 1335 },
                items: 3
              },
              tablet: {
                breakpoint: { max: 1334, min: 995 },
                items: 2
              },
              mobile: {
                breakpoint: { max: 994, min: 0 },
                items: 1
              }
            }}
          />
        ))}
      <GoogleReviews className="fade-in" backgroundColor="#fff" />
      {/* <Testimonials backgroundColor={windowWidth < 590 ? '#fff' : 'var(--connolly-grey)'} /> */}
    </>
  );
}

const SimilarDoorPartsMobile = styled.div`
  align-items: center;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding-bottom: 40px;

  @media (width >= 590px) {
    background-color: var(--connolly-grey);
  }

  > h3 {
    font-size: 2rem;
    padding: 1.25em 0 0.125em;
    text-align: center;
  }

  a {
    height: 30.375rem;
    width: 21.25rem;

    .gatsby-image-wrapper {
      height: 23.75rem;
    }
  }
`;

const StyledInnerGrid = styled(InnerGrid)`
  /* Carousel */
  > :nth-child(1) {
    grid-column: 1/-1;

    @media (width >= 900px) {
      grid-column: 1/7;
    }

    @media (width >= 1100px) {
      grid-column: 1/6;
    }
  }
`;

const StyledMultiColumnCarousel = styled(MultiColumnCarousel)`
  padding-bottom: 112px;
  width: 100%;

  > h3 {
    padding: 1.25em 0 0.85em;

    @media (width >= 768px) {
      padding: 1.975em 0 1.25em;
    }
  }

  > div {
    /* Width of Arrow Buttons */
    max-width: 360px;

    /* Width of Slides container */
    > div {
      max-width: 360px;
    }
  }

  @media (width >= 380px) {
    > div {
      max-width: 440px;

      > div {
        max-width: 380px;
      }
    }
  }

  @media (width >= 914px) {
    padding-bottom: 85px;

    > div {
      max-width: 820px;

      > div {
        max-width: 760px;
      }
    }
  }

  @media (width >= 1300px) {
    > div {
      max-width: 1200px;

      > div {
        max-width: 1140px;
      }
    }
  }

  .react-multi-carousel-dot button {
    border: none;
    width: 0.625rem;
    height: 0.625rem;
  }

  .react-multi-carousel-dot--active button {
    background-color: var(--connolly-red);
  }

  .react-multi-carousel-item a {
    height: 30.375rem;
    width: 21.25rem;

    .gatsby-image-wrapper {
      height: 23.75rem;
    }
  }
`;

export const query = graphql`
  query ($id: String!, $productCollection: [String!]) {
    heroData: markdownRemark(
      frontmatter: { productCollectionName: { eq: "All Stair And Door Parts" } }
    ) {
      id
      frontmatter {
        productPageHeroImage {
          name
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
          }
        }
      }
    }
    doorPart: sanityDoorPart(id: { eq: $id }) {
      id
      title
      finish
      productCollection
      description
      images {
        _key
        hotspot {
          height
          width
          x
          y
        }
        crop {
          top
          bottom
          left
          right
        }
        asset {
          _id
          originalFilename
          url
          metadata {
            dimensions {
              width
              height
            }
          }
        }
      }
      productVariants {
        default {
          productId
          dimensions
        }
        additional {
          productId
          dimensions
        }
      }
    }
    doorPartsFromTheSameProductCollection: allSanityDoorPart(
      filter: { productCollection: { in: $productCollection }, id: { ne: $id } }
    ) {
      nodes {
        id
        title
        images {
          _key
          hotspot {
            height
            width
            x
            y
          }
          crop {
            top
            bottom
            left
            right
          }
          asset {
            _id
            originalFilename
            url
            metadata {
              dimensions {
                width
                height
              }
            }
          }
        }
        productVariants {
          default {
            productId
          }
        }
      }
    }
  }
`;

export function Head({ data, location }) {
  const { doorPart } = data;
  return (
    <SEO title={`${doorPart.title} | Door Part`} description={doorPart.description}>
      <ProductStructuredData
        title={doorPart.title}
        description={doorPart.description}
        images={doorPart.images}
        sku={doorPart.productVariants.default.productId}
        url={`https://www.connollystairs.ie${location.pathname}`}
        category={doorPart.productCollection?.join(', ')}
        height={doorPart.productVariants.default.height}
        width={doorPart.productVariants.default.width}
        depth={doorPart.productVariants.default.depth}
      />
      <meta name="view-transition" content="same-origin" />
    </SEO>
  );
}

export default DoorPartTemplate;
